import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getCurrentDateBR, getCurrentDateBRAsDate, convertBRDateToISO } from '@/utils/dateUtils';
import { 
  BarChart3, 
  Users, 
  TrendingUp,
  Calendar,
  RefreshCw,
  RotateCcw,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import DateRangeSelector from './DateRangeSelector';
import DisparosStatsCards from './DisparosStatsCards';
import ClientMetricsDetailModal from './modals/ClientMetricsDetailModal';
import dashboardApi from '@/services/dashboardApi';
import { apiService } from '@/lib/api';
import { DashboardStats, PeriodOption } from '@/types/dashboard';
import { useDisparos, DisparosProvider } from '@/contexts/DisparosContext';

// Interface para a resposta da API de sincronização
interface SyncConnectionsResponse {
  stats?: {
    synced: number;
    skipped: number;
    errors: number;
  };
  message?: string;
}

// Componente interno que usa o contexto
const ClientDisparosDashboardContent: React.FC = () => {
  const { user } = useAuth();
  const { clienteId } = useDisparos(); // Para admins selecionarem cliente
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Estado do modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'pendentes' | 'enviados' | 'invalidos' | 'falhas' | 'baixados'>('pendentes');
  const [modalTitle, setModalTitle] = useState('');
  const [modalIcon, setModalIcon] = useState<any>(Clock);
  const [modalColor, setModalColor] = useState('bg-yellow-500');
  
  console.log('🚀 [ClientDisparosDashboard] Componente renderizado');
  console.log('🚀 [ClientDisparosDashboard] User:', user);

  // CORREÇÃO: Calcular data atual dinamicamente a cada renderização
  const getCurrentPeriod = useCallback((): PeriodOption => {
    const dataAtual = getCurrentDateBR();
    return {
      label: 'Hoje',
      value: 'today',
      startDate: dataAtual,
      endDate: dataAtual
    };
  }, []);

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>(getCurrentPeriod());

  // CORREÇÃO: Atualizar período "Hoje" quando o componente for montado ou quando necessário
  useEffect(() => {
    const currentPeriod = getCurrentPeriod();
    setSelectedPeriod(prevPeriod => {
      // Se o período atual é "Hoje", sempre atualizar com a data atual
      if (prevPeriod.value === 'today') {
        console.log('🕐 [ClientDisparosDashboard] Atualizando período "Hoje" com data atual:', currentPeriod.startDate);
        return currentPeriod;
      }
      return prevPeriod;
    });
  }, [getCurrentPeriod]);

  // Função para carregar dados do dashboard
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('📊 [ClientDisparosDashboard] Carregando dados com período:', {
        startDate: selectedPeriod.startDate,
        endDate: selectedPeriod.endDate,
        label: selectedPeriod.label
      });
      
      // Se o período for "Hoje", não enviar filtro de data para mostrar todos os dados
      const dateRange = selectedPeriod.value === 'today' ? undefined : {
        startDate: selectedPeriod.startDate,
        endDate: selectedPeriod.endDate
      };
      
      // Determinar clientId: clientes usam seu próprio, admins usam o selecionado
      let targetClientId: number | undefined;
      if (user?.role === 'admin') {
        if (!clienteId) {
          console.log('⏳ [ClientDisparosDashboard] Admin sem cliente selecionado, não carregando dados');
          return;
        }
        targetClientId = clienteId;
      } else if (user?.role === 'cliente') {
        targetClientId = user.client_id;
      }

      console.log('📊 [ClientDisparosDashboard] Cliente alvo:', targetClientId);
      
      const data = await dashboardApi.getDashboardStats(dateRange, targetClientId);
      
      console.log('📊 [ClientDisparosDashboard] Dados recebidos:', data);
      console.log('📊 [ClientDisparosDashboard] Tipo dos dados:', typeof data);
      console.log('📊 [ClientDisparosDashboard] Data keys:', Object.keys(data || {}));
      
      // Verificar se os dados são válidos antes de definir
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        setStats(data);
        setHasLoadedOnce(true);
        console.log('📊 [ClientDisparosDashboard] Stats definidos no estado com sucesso');
      } else {
        console.log('⚠️ [ClientDisparosDashboard] Dados inválidos ou vazios recebidos');
        // Se não há dados válidos, manter stats como null mas marcar como carregado
        setHasLoadedOnce(true);
      }
    } catch (error) {
      console.error('❌ [ClientDisparosDashboard] Erro ao carregar dados do dashboard:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do dashboard. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod, user, clienteId]);

  // Função para sincronizar caixas do ConnectChat
  const handleSyncConnectChatBoxes = async () => {
    if (!user?.client?.id || !user?.client?.account_id) {
      toast({
        title: "Erro",
        description: "Cliente não possui Account ID configurado para sincronização.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSyncing(true);
      
      const response = await apiService.post<SyncConnectionsResponse>('/connectchat/sync-connections', {
        accountId: user.client.account_id,
        clientId: user.client.id
      });

      // Corrige: response pode não ter 'stats', então faz checagem segura
      const stats = response?.stats || { synced: 0, skipped: 0, errors: 0 };

      toast({
        title: "Sincronização Concluída",
        description: `Sincronizadas ${stats.synced ?? 0} novas caixas, ${stats.skipped ?? 0} ignoradas, ${stats.errors ?? 0} erros.`,
        variant: stats.errors > 0 ? "destructive" : "default"
      });

      // Recarregar dados do dashboard após sincronização
      await loadDashboardData();

    } catch (error) {
      console.error('Erro ao sincronizar caixas do ConnectChat:', error);
      toast({
        title: "Erro na Sincronização",
        description: "Não foi possível sincronizar as caixas do ConnectChat. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    console.log('🔄 [ClientDisparosDashboard] useEffect executado - carregando dados iniciais');
    console.log('🔄 [ClientDisparosDashboard] User:', user);
    console.log('🔄 [ClientDisparosDashboard] SelectedPeriod:', selectedPeriod);
    loadDashboardData();
  }, [loadDashboardData]);

  // Atualização automática removida - usuário pode atualizar manualmente
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     loadDashboardData();
  //   }, 3 * 60 * 1000); // 3 minutos

  //   return () => clearInterval(interval);
  // }, [loadDashboardData]);

  // Função para atualizar manualmente
  const handleRefresh = () => {
    loadDashboardData();
    toast({
      title: "Atualizado",
      description: "Dados do dashboard atualizados com sucesso!",
    });
  };

  // Função para mudar período
  const handlePeriodChange = (period: PeriodOption) => {
    console.log('📅 [ClientDisparosDashboard] Mudando período para:', period);
    setSelectedPeriod(period);
  };

  // Função para abrir modal com detalhes
  const openMetricModal = (type: 'pendentes' | 'enviados' | 'invalidos' | 'falhas' | 'baixados') => {
    const configs = {
      pendentes: {
        title: 'Pendentes',
        icon: Clock,
        color: 'bg-yellow-500'
      },
      enviados: {
        title: 'Enviados',
        icon: CheckCircle2,
        color: 'bg-green-500'
      },
      invalidos: {
        title: 'Inválidos',
        icon: AlertCircle,
        color: 'bg-orange-500'
      },
      falhas: {
        title: 'Falhas',
        icon: XCircle,
        color: 'bg-red-500'
      },
      baixados: {
        title: 'Baixados',
        icon: TrendingUp,
        color: 'bg-blue-500'
      }
    };

    const config = configs[type];
    setModalType(type);
    setModalTitle(config.title);
    setModalIcon(config.icon);
    setModalColor(config.color);
    setModalOpen(true);
  };

  // Mostrar loading apenas se nunca carregou dados E está carregando
  if (isLoading && !hasLoadedOnce) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <AtomicLoadingScreen 
          message="Carregando dados do dashboard..." 
          className="h-64"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com boas-vindas */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-montserrat font-bold text-gray-900">
                Dashboard de Disparos
              </h2>
              <p className="text-gray-600 mt-1">
                {user?.client?.legal_name} - {user?.client?.trade_name}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Última atualização: {format(new Date(), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Botão de Sincronizar Caixas ConnectChat */}
              <Button
                onClick={handleSyncConnectChatBoxes}
                disabled={isSyncing || !user?.client?.account_id}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RotateCcw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Sincronizando...' : 'Sincronizar Caixas'}
              </Button>
              
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Seletor de período */}
      <DateRangeSelector
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />

      {/* Resumo do dia atual */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Resumo do Dia Atual
          </CardTitle>
          <CardDescription>
            Atividade de disparos de hoje
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.resumo_hoje && stats.resumo_hoje.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.resumo_hoje.map((item, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 capitalize">
                      {item.tipo_envio.toLowerCase()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      item.status_envio === 'ENVIADO' ? 'bg-green-100 text-green-800' :
                      item.status_envio === 'PENDENTE' ? 'bg-yellow-100 text-yellow-800' :
                      item.status_envio === 'CONTATO_INVALIDO' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status_envio}
                    </span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {item.quantidade}
                  </div>
                  <div className="text-sm text-gray-600">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(item.valor_total)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhuma atividade registrada hoje</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cards de estatísticas */}
      {stats && <DisparosStatsCards stats={stats} onCardClick={openMetricModal} />}

      {/* Performance do Sistema */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Performance do Sistema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="font-medium text-green-800">Taxa de Envio</span>
              <span className="text-green-600 font-semibold">
                {stats && stats.status_agrupados?.enviados > 0 && stats.total_envios > 0
                  ? Math.round((stats.status_agrupados.enviados / stats.total_envios) * 100)
                  : 0}%
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="font-medium text-blue-800">Taxa de Baixa</span>
              <span className="text-blue-600 font-semibold">
                {stats && stats.status_agrupados?.baixados > 0 && stats.total_envios > 0
                  ? Math.round((stats.status_agrupados.baixados / stats.total_envios) * 100)
                  : 0}%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Detalhes das Métricas */}
      <ClientMetricsDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        metricType={modalType}
        title={modalTitle}
        icon={modalIcon}
        color={modalColor}
        period={(() => {
          // Mapear usando o value em vez do label
          const periodMap: { [key: string]: string } = {
            'today': 'today',
            'last7days': '7d',
            'last30days': '30d',
            'thisMonth': 'custom',
            'lastMonth': 'custom'
          };
          
          const mappedPeriod = periodMap[selectedPeriod?.value || 'today'] || 'custom';
          
          console.log('🔍 [ClientDisparosDashboard] Mapeando período para modal:', {
            originalValue: selectedPeriod?.value,
            originalLabel: selectedPeriod?.label,
            mappedPeriod,
            startDate: selectedPeriod?.startDate,
            endDate: selectedPeriod?.endDate
          });
          
          return mappedPeriod;
        })()}
        customStartDate={selectedPeriod?.startDate ? convertBRDateToISO(selectedPeriod.startDate) : undefined}
        customEndDate={selectedPeriod?.endDate ? convertBRDateToISO(selectedPeriod.endDate) : undefined}
      />
    </div>
  );
};

// Componente wrapper que garante o contexto
const ClientDisparosDashboard: React.FC = () => {
  const { user } = useAuth();
  
  return (
    <DisparosProvider user={user} clienteId={user?.client_id} statusFilter="all">
      <ClientDisparosDashboardContent />
    </DisparosProvider>
  );
};

export default ClientDisparosDashboard;
